import React, { useEffect, useState } from 'react';
import { Button } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

//application type definitions
import { AddPartMode } from '../../models/AddPartMode';
import { UnitDefinition, Part, SystemType, GetSystemResponse } from '../../models/Api';

//application services
import { createGroup, createPart, getUniqueName, isPDNDivision } from '../../utils/Utils';
import GroupStateService from '../../services/GroupStateService';
import SizingToolService, { SizingToolState, TabState } from '../../services/SizingToolService';
import { SystemProjectsSummary } from '../../components/SystemsProjectsSummary/SystemProjectsSummary';
import SystemService, { SystemState } from '../../services/SystemService';
import { useTranslation } from 'react-i18next';
import UnitService from '../../services/UnitService';

//application components
import { AddPartModal } from '../../components/AddPartModal/AddPartModal';
import { Breadcrumb } from '../../components/Breadcrumb/Breadcrumb';
import { DashboardCanvas } from '../../components/DashboardCanvas/DashboardCanvas';
import MiniSizingDialog from '../../components/MiniSizingDialog/MiniSizingDialog';
import { RightPane } from '../../components/RightPane/RightPane';
import { SystemCard } from '../../components/SystemCard/SystemCard';
import CreateSystemProjectDialog from '../../components/CreateSystemProjectDialog/CreateSystemProjectDialog';

import styles from './CleanDash.module.scss';
import DialogBox from '../../components/DialogBox/DialogBox';
import Constants from '../../utils/Constants';

export enum CreateDialogType {
  System = 1,
  Project,
}

export type CreateDialogStateType = {
  show: boolean;
  type: CreateDialogType | undefined;
  projectId: string | null | undefined;
};

export default function CleanDash(props: { handleLaunchSizingTool: (system: GetSystemResponse | undefined, part: Part) => void; tabState: TabState }) {
  const { t } = useTranslation();
  const [systemState, setSystemState] = useState<SystemState>({});
  const [showAddPartModal, setShowAddPartModal] = useState(false);
  const [showSizingAlertDialog, setShowSizingAlertDialog] = useState(false);
  const [airflowDirection, setAirflowDirection] = useState<boolean>();
  const [sizingToolState, setSizingToolState] = useState<SizingToolState>({
    part: {} as Part,
    showMiniSizing: false,
    createPartSizingData: false,
    sizingUnderway: false,
  });
  const [unitDefinitions, setUnitDefinitions] = useState<UnitDefinition[]>([]);
  const [showSystemsProjectsModal, setShowSystemsProjectsModal] = useState(false);

  const [createDialogState, setCreateDialogState] = useState<CreateDialogStateType>({
    show: false,
    type: undefined,
    projectId: undefined,
  });
  const [systemTypesAndTemplates, setSystemTypesAndTemplates] = useState<SystemType[] | undefined>([] as SystemType[]);

  const isSizingTabOpen = () => {
    return SizingToolService.getTabOpenState();
  };

  const openSizingAlertDialog = () => {
    setShowSizingAlertDialog(true);
  };

  const fetchSystemTypesAndTemplates = async () => {
    const response = await SystemService.getSystemTypesAndTemplates();
    setSystemTypesAndTemplates(response);
  };

  const FLOW_TO_RIGHT = true;

  async function autoCreateGroupAndPart() {
    const system = systemState.system as GetSystemResponse;
    const groupUniqueName = sessionStorage.getItem(Constants.sessionStorageKeys.autoCreateGroupUniqueName);
    const selectedGroupDefinition = system?.groupDefinitions?.find((groupDefinition) => groupDefinition.uniqueName === groupUniqueName);
    const createGroupResponse = await createGroup(
      system.id as string,
      selectedGroupDefinition?.id as number,
      getUniqueName(system, selectedGroupDefinition?.displayName as string, true, false),
      false,
      null,
    );

    if (createGroupResponse.status === 200) {
      const groupId = createGroupResponse.data.id as number;
      const partCategoryName = sessionStorage.getItem(Constants.sessionStorageKeys.autoCreatePartCategoryName);
      const selectedPartCategory = system.partCategories?.find((partCategory) => partCategory.name === partCategoryName);
      const baseName = selectedPartCategory?.options?.find((option) => option.addOnDashboard)?.displayShortName;
      const newPart = await createPart(groupId, false, selectedPartCategory?.id as number, getUniqueName(system, baseName as string, false, false), 1, 1, 1);

      if (newPart) {
        props.handleLaunchSizingTool(system, newPart);
      }
    }

    sessionStorage.removeItem(Constants.sessionStorageKeys.autoCreateGroupUniqueName);
    sessionStorage.removeItem(Constants.sessionStorageKeys.autoCreatePartCategoryName);
  }

  const autoExecAction = () => {
    const action = sessionStorage.getItem(Constants.sessionStorageKeys.autoExecAction);
    if (!action) {
      return;
    }
    if (action === Constants.autoExecActions.showAddPartDialog) {
      setShowAddPartModal(true);
    }
    if (action === Constants.autoExecActions.launchSizingTool) {
      autoCreateGroupAndPart();
    }
    sessionStorage.removeItem(Constants.sessionStorageKeys.autoExecAction);
  };

  useEffect(() => {
    UnitService.getState(setUnitDefinitions);
    SystemService.getState(setSystemState);
    SizingToolService.startMessageHandler();
    SizingToolService.getState(setSizingToolState);
    fetchSystemTypesAndTemplates();

    return () => {
      SizingToolService.stopMessageHandler();
    };
  }, []);

  useEffect(() => {
    setAirflowDirection(systemState.system?.isRightToLeft);
    if (systemState.system) {
      autoExecAction();
    }
  }, [systemState]);

  useEffect(() => {
    if (systemState.system && unitDefinitions) GroupStateService.fetchGroups(systemState.system, unitDefinitions);
  }, [systemState.system, unitDefinitions]);

  const closeMiniSizingModal = () => {
    setSizingToolState({
      part: {} as Part,
      showMiniSizing: false,
      createPartSizingData: false,
      sizingUnderway: false,
    });
  };

  return (
    <div className="clean-dash">
      <DialogBox
        title={t('OnlyOnePartCanBeEdited')}
        message={t('ExistingSizingTabAlert')}
        showModalState={showSizingAlertDialog}
        closeAction={() => {
          setShowSizingAlertDialog(false);
        }}
      />
      <MiniSizingDialog
        part={sizingToolState.part}
        showMiniSizingModal={sizingToolState.showMiniSizing}
        createPartSizingData={sizingToolState.createPartSizingData}
        closeMiniSizingModal={closeMiniSizingModal}
        system={systemState.system}
      />
      <AddPartModal
        system={systemState.system}
        modalState={showAddPartModal}
        mode={AddPartMode.InitialGroupAndPart}
        changeModalState={(show: boolean) => setShowAddPartModal(show)}
      />
      {systemState.system ? (
        <>
          <Breadcrumb
            system={systemState.system}
            showSystemProjectsModal={() => {
              setShowSystemsProjectsModal(true);
            }}
          />
          <div className={styles['dashboard-panes']}>
            <div className={styles['right-pane']}>
              <RightPane
                system={systemState.system}
                divisionId={systemState.system.divisionId as number}
                displayInMetric={systemState.system.displayInMetricUnits as boolean}
                units={unitDefinitions as UnitDefinition[]}
                isSizingTabOpen={isSizingTabOpen}
                openSizingAlertDialog={openSizingAlertDialog}
              />
            </div>
            <div className={styles['left-pane']}>
              <SystemCard system={systemState.system} />
              {systemTypesAndTemplates && (
                <CreateSystemProjectDialog
                  systemTypesAndTemplates={systemTypesAndTemplates}
                  dialogState={createDialogState}
                  closeAction={() => {
                    setCreateDialogState({
                      show: false,
                      type: undefined,
                      projectId: undefined,
                    });
                  }}
                />
              )}

              <SystemProjectsSummary
                currentSystem={{
                  id: systemState.system.id,
                  projectName: systemState.system.projectName,
                  projectId: systemState.system.projectId,
                }}
                showSystemsProjectsModal={showSystemsProjectsModal}
                openAction={(type, projectId) => {
                  setCreateDialogState({
                    show: true,
                    type: type,
                    projectId: projectId,
                  });
                }}
                closeAction={() => {
                  setShowSystemsProjectsModal(false);
                }}
              />
              <div className={styles['dashboard-actions']}>
                <Button onClick={() => setShowAddPartModal(true)} title={t('CreateNewGroupAndPart')} variant="outlined" className={styles['add-parts-button']}>
                  <FontAwesomeIcon icon="plus" />
                </Button>
                {isPDNDivision(systemState?.system?.divisionId) && (
                  <div className={styles['airflow-action']}>
                    <div className={styles['airflow-direction']}>
                      {airflowDirection === FLOW_TO_RIGHT ? (
                        <span className={styles['arrow-right-pointer']} />
                      ) : (
                        <span className={styles['arrow-left-pointer']} />
                      )}
                      <div className={styles['flow-text']}>{t('Flow')}</div>
                    </div>

                    <Button
                      variant="outlined"
                      className={styles['switch-airflow-button']}
                      onClick={async () => {
                        const status = await SystemService.updateAirflowDirection(!airflowDirection);
                        if (status) {
                          setAirflowDirection(!airflowDirection);
                        }
                      }}
                    >
                      {t('SwitchAirflow')}
                    </Button>
                  </div>
                )}
              </div>
              <DashboardCanvas
                system={systemState.system}
                unitDefinitions={unitDefinitions as UnitDefinition[]}
                handleLaunchSizingTool={props.handleLaunchSizingTool}
                isSizingTabOpen={isSizingTabOpen}
                openSizingAlertDialog={openSizingAlertDialog}
              />
            </div>
          </div>
        </>
      ) : (
        <div className={styles['dashboard-loading']}>{t('LoadingSystem')}</div>
      )}
    </div>
  );
}
